// SPDX-License-Identifier: AGPL-3.0-or-later

import {ResettableStates} from '@app/features/app/state/ResettableStates';
import {LimitResolver} from '@app/features/app/utils/LimitResolverAdapter';
import Guilds from '@app/features/guild/state/Guilds';
import * as GuildTagCommands from '@app/features/guild_tag/commands/GuildTagCommands';
import {AccountScopedWork} from '@app/features/platform/state/AccountScopedWork';
import type {GuildTagResponse} from '@fluxer/schema/src/domains/guild/GuildTagSchemas';
import {makeAutoObservable, observable} from 'mobx';

const BATCH_WINDOW_MS = 250;
const MAX_IDS_PER_REQUEST = 50;
const MAX_GUILDS = 5000;
const TAG_MAX_AGE_MS = 3_600_000;
const FAILED_RETRY_MS = 60_000;

export interface GuildTagView {
	tag: string;
	badge: number | null;
}

// A user carries only the guild ID, so a tag change costs one guild write and no write per wearer. A guild the user is
// in comes live with GUILD_UPDATE; any other is read by ID, at most once an hour, so a renamed tag reaches rows outside
// the guild within that hour.
class GuildTags {
	private readonly tags = observable.map<string, GuildTagView | null>({}, {deep: false});
	private generation = 0;
	private freshUntil = new Map<string, number>();
	private queue: Array<string> = [];
	private timer: ReturnType<typeof setTimeout> | null = null;

	constructor() {
		makeAutoObservable<GuildTags, 'tags' | 'generation' | 'freshUntil' | 'queue' | 'timer'>(
			this,
			{tags: false, generation: false, freshUntil: false, queue: false, timer: false},
			{autoBind: true},
		);
	}

	get enabled(): boolean {
		return LimitResolver.resolve({key: 'feature_guild_tags', fallback: 0}) > 0;
	}

	tagFor(guildId: string): GuildTagView | null {
		const guild = Guilds.getGuild(guildId);
		if (guild) return guild.guildTag ? {tag: guild.guildTag, badge: guild.guildTagBadge} : null;
		this.ask(guildId);
		return this.tags.get(guildId) ?? null;
	}

	reset(): void {
		this.generation++;
		if (this.timer !== null) clearTimeout(this.timer);
		this.timer = null;
		this.queue = [];
		this.freshUntil.clear();
		this.tags.clear();
	}

	// Called while rendering, so it only touches the unobserved queue; the answer lands later through settle.
	private ask(guildId: string): void {
		if (AccountScopedWork.isSuspended) return;
		const now = Date.now();
		if ((this.freshUntil.get(guildId) ?? 0) > now) return;
		this.freshUntil.delete(guildId);
		this.freshUntil.set(guildId, now + TAG_MAX_AGE_MS);
		this.queue.push(guildId);
		this.timer ??= setTimeout(this.flush, BATCH_WINDOW_MS);
	}

	private settle(ids: ReadonlyArray<string>, answers: ReadonlyArray<GuildTagResponse>): void {
		const byId = new Map(answers.map((answer) => [answer.id, answer]));
		for (const id of ids) {
			if (!this.freshUntil.has(id)) continue;
			const answer = byId.get(id);
			this.tags.set(id, answer ? {tag: answer.guild_tag, badge: answer.guild_tag_badge} : null);
		}
		this.dropOldest();
	}

	// Both maps share the cap, oldest asked first. Runs when an answer lands, never while a row renders.
	private dropOldest(): void {
		for (const id of this.freshUntil.keys()) {
			if (this.freshUntil.size <= MAX_GUILDS) return;
			this.freshUntil.delete(id);
			this.tags.delete(id);
		}
	}

	private flush(): void {
		const generation = this.generation;
		const ids = this.queue;
		this.queue = [];
		this.timer = null;
		for (let start = 0; start < ids.length; start += MAX_IDS_PER_REQUEST) {
			const chunk = ids.slice(start, start + MAX_IDS_PER_REQUEST);
			GuildTagCommands.fetchTags(chunk).then(
				(answers) => {
					if (generation === this.generation) this.settle(chunk, answers);
				},
				() => {
					if (generation !== this.generation) return;
					const retryAt = Date.now() + FAILED_RETRY_MS;
					for (const id of chunk) if (this.freshUntil.has(id)) this.freshUntil.set(id, retryAt);
					this.dropOldest();
				},
			);
		}
	}
}

const guildTags = new GuildTags();
ResettableStates.register(guildTags, guildTags.reset);
AccountScopedWork.registerCancellation(() => guildTags.reset());

export default guildTags;
